import logo from '../new_winston_logo.svg';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import supabase from '../supabase';
import jsPDF from 'jspdf';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton } from '../components/adminTheme';
import { useLang } from '../useLang';

function Agreement() {
    const navigate = useNavigate();
    const { t } = useLang();
    const [agreementDate, setAgreementDate] = useState('');
    const today = new Date().toLocaleDateString('en-CA');
    const [fullName, setFullName] = useState('');
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');

    const svgToBase64 = (svgUrl) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || 800;
            canvas.height = img.naturalHeight || 218;
            canvas.getContext('2d').drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.src = svgUrl;
    });

    const handleDownloadPDF = async () => {
        const doc = new jsPDF('p', 'mm', 'a4');
        const W = 210;
        const margin = 20;
        const contentW = W - margin * 2;
        let y = 20;

        const logoBase64 = await svgToBase64(logo);

        const addSectionTitle = (title) => {
            if (y > 240) { doc.addPage(); y = 20; }
            y += 6;
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.3);
            doc.line(margin, y, W - margin, y);
            y += 5;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(150, 150, 150);
            doc.setCharSpace(1.5);
            doc.text(title.toUpperCase(), margin, y);
            doc.setCharSpace(0);
            y += 7;
        };

        const addWrappedText = (text, size, style = 'normal', color = [60, 60, 60], lineH = 6.5) => {
            doc.setFontSize(size);
            doc.setFont('helvetica', style);
            doc.setTextColor(...color);
            const lines = doc.splitTextToSize(text, contentW);
            lines.forEach(line => {
                if (y > 270) { doc.addPage(); y = 20; }
                doc.text(line, margin, y);
                y += lineH;
            });
        };

        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 26, 26);
        doc.text('Contributor Agreement', margin, y);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 150, 150);
        doc.setCharSpace(1.2);
        doc.text('WINSTON.IE — LONGDEN COLLECTIVE', margin, y + 7);
        doc.setCharSpace(0);

        doc.addImage(logoBase64, 'PNG', W - margin - 44, y - 6, 44, 12);

        y += 18;
        doc.setDrawColor(26, 26, 26);
        doc.setLineWidth(0.6);
        doc.line(margin, y, W - margin, y);
        y += 10;

        doc.setFillColor(247, 245, 242);
        doc.setDrawColor(232, 228, 222);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, contentW, 22, 2, 2, 'FD');

        const cols = [margin + 5, margin + 50, margin + 100, margin + 145];
        const labels = ['PREPARED FOR', 'CREATED BY', 'DATE', 'PLATFORM'];
        const values = [fullName, 'Longden Collective', today, 'winston.ie'];

        labels.forEach((label, i) => {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(160, 160, 160);
            doc.setCharSpace(0.8);
            doc.text(label, cols[i], y + 7);
            doc.setCharSpace(0);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(26, 26, 26);
            doc.text(values[i], cols[i], y + 14);
        });
        y += 30;

        doc.setDrawColor(26, 26, 26);
        doc.setLineWidth(1.2);
        const preamble = `This Contributor Agreement (the "Agreement") is entered into as of ${today}, between winston.ie, a label of Longden Collective ("the Platform"), and the contributor identified above ("Contributor"). By publishing creative work on winston.ie, the Contributor acknowledges having read, understood, and agreed to the terms set forth herein.`;
        const preambleLines = doc.splitTextToSize(preamble, contentW - 8);
        const preambleHeight = preambleLines.length * 6.5 + 6;
        doc.line(margin, y, margin, y + preambleHeight);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        preambleLines.forEach(line => {
            doc.text(line, margin + 6, y + 6);
            y += 6.5;
        });
        y += 12;

        const sections = [
            ['1 — PUBLIC BRAND & ADMINISTRATIVE OWNERSHIP', "winston.ie is the public-facing brand under which this platform operates. Administratively, and for purposes of copyright, attribution, and internal organization, winston.ie is a label of Longden Collective. This distinction exists for organizational clarity: contributors and visitors interact with winston.ie as the visible identity, while Longden Collective represents the broader creative ecosystem under which winston.ie, throughmyeyes, and Ardoise each operate as distinct labels."],
            ['2 — COPYRIGHT & ATTRIBUTION', 'The Contributor retains full copyright over all work published on winston.ie. Publishing on this platform does not transfer intellectual property ownership to winston.ie, Longden Collective, or any third party. The Contributor affirms that all submitted work is original and does not infringe upon the rights of any third party. The Contributor will always be credited as the original creator on all content published, shared, or distributed through winston.ie or on its behalf, using the name provided at the time of publishing, which may not be altered without the Contributor\'s consent. Published works will carry the notice "© [Creator Name] — winston.ie 2026." Site-wide content and platform design carry the separate notice "© winston.ie 2026. All rights reserved. A Longden Collective label."'],
            ['3 — CREATIVE CATALOGUE', "By publishing on winston.ie, the Contributor's work becomes part of the official winston.ie creative catalogue. winston.ie may present, submit, and exhibit this work as part of its institutional portfolio in competitions, festivals, partnerships, and exhibitions — with full credit attributed to the Contributor as the original creator at all times."],
            ['4 — LICENCE & PRIMARY PUBLISHING RIGHT', "The Contributor grants winston.ie / Longden Collective a worldwide, royalty-free licence to publish, display, share, distribute, and use the work as part of the winston.ie platform and its associated projects — including partnerships, collaborations, promotional campaigns, and external exhibitions. This licence remains in effect for the duration of the work's presence on the platform and constitutes a primary, non-exclusive publishing right: the Contributor retains full rights to share, republish, or display the same work on personal social media accounts, portfolios, resumes, or CVs. The Contributor agrees not to publish the same work through another official photography distribution platform, stock agency, or competing curated platform without prior written permission from winston.ie."],
            ['5 — INTERMEDIARY & REPRESENTATION', "winston.ie acts as the Contributor's label and sole representative for any third-party requests involving published work — including usage requests, reproduction, licensing to external parties, and collaboration opportunities. Any such request will be communicated to the Contributor. The Contributor's explicit written consent is required before any external use is approved. winston.ie will not act on the Contributor's behalf without prior knowledge and consent."],
            ['6 — COMMERCIAL REVENUE', "If published work generates commercial revenue through winston.ie — whether through licensing, partnerships, sales, or other means — a revenue-sharing arrangement will be discussed and agreed upon separately with the Contributor prior to any commercial transaction. winston.ie commits to full transparency in all revenue matters involving Contributor work."],
            ['7 — CONTENT STANDARDS', "The Contributor agrees that submitted content is original work, does not infringe on third-party rights, and complies with winston.ie's community and content guidelines as published from time to time."],
            ['8 — REMOVAL & WITHDRAWAL', "The Contributor may request removal of their contributed work at any time by contacting winston.ie at contact@winston.longden.site. Removal requests will be processed within 14 days of receipt. Upon removal, the licence granted under Section 4 is terminated for future use; existing third-party agreements entered into prior to the removal request remain valid and enforceable."],
            ['9 — NO EMPLOYMENT OR PARTNERSHIP', "Nothing in this Agreement establishes an employment, partnership, or agency relationship between the Contributor and winston.ie or Longden Collective."],
            ['10 — GOVERNING LAW', "This Agreement is governed by the laws of the Province of Quebec and the federal laws of Canada applicable therein. Any disputes arising under this Agreement shall be resolved in the courts of Montreal, Quebec."],
            ['11 — OPERATING STATUS', "Longden Collective and winston.ie are not, as of the date of this Agreement, legally incorporated entities. This Agreement is made in good faith between the Contributor and the sole owner and operator of these platforms, Andy Le. Should Longden Collective or winston.ie become a formally incorporated entity in the future, this Agreement and its terms will carry forward under that entity without requiring re-signature, unless the Contributor is notified otherwise."],
            ['12 — GOVERNING LANGUAGE', "This Agreement is available in French, English, and Vietnamese for the Contributor's convenience. In the event of any discrepancy between versions, the version accepted by the Contributor on the platform shall govern."],
        ];

        sections.forEach(([title, body]) => {
            addSectionTitle(title);
            addWrappedText(body, 11, 'normal', [42, 42, 42]);
            y += 4;
        });

        y += 8;
        if (y > 220) { doc.addPage(); y = 24; }

        doc.setDrawColor(26, 26, 26);
        doc.setLineWidth(0.6);
        doc.line(margin, y, W - margin, y);
        y += 10;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(150, 150, 150);
        doc.setCharSpace(1.5);
        doc.text('ACCEPTANCE', margin, y);
        doc.setCharSpace(0);
        y += 8;

        addWrappedText(
            `By creating an account and publishing content on winston.ie, ${fullName} has agreed to these Contributor Terms as of ${today}. No physical signature is required — continued use of the platform constitutes full acceptance of this agreement.`,
            11, 'normal', [42, 42, 42]
        );

        y = 275;
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        doc.line(margin, y, W - margin, y);
        y += 6;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(180, 180, 180);
        doc.text('© winston.ie 2026. All rights reserved. A Longden Collective label.', W / 2, y, { align: 'center' });
        doc.text('contact@winston.longden.site — longden.site', W / 2, y + 5, { align: 'center' });

        doc.save(`winston-ie-agreement-${fullName.replace(/ /g, '_') || 'contributor'}.pdf`);
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/admin');
    };

    useEffect(() => { document.title = 'Agreement — winston.ie Studio'; }, []);

    useEffect(() => {
        const fetchUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) { navigate('/admin'); return; }
            const { data: userData } = await supabase
                .from('users')
                .select('username, first_name, last_name, created_on, role, avatar_url')
                .eq('email', data.session.user.email)
                .single();

            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setAvatarUrl(userData.avatar_url || '');
                setFullName(`${userData.first_name} ${userData.last_name}`);
                setAgreementDate(userData.created_on?.split('T')[0]);
            }
        };
        fetchUser();
    }, [navigate]);

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .agreement-scope * {
                    box-sizing: border-box;
                }

                .cagreement-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .cagreement-content { margin-left: 0; padding-top: 60px; }
                }

                .doc-page {
                    background: white;
                    max-width: 760px;
                    margin: 0 auto;
                    padding: 72px 80px 80px;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    color: #1a1a1a;
                    line-height: 1.75;
                    font-size: 15px;
                    font-weight: 500;
                }
                .doc-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 48px;
                    padding-bottom: 32px;
                    border-bottom: 2px solid #1a1a1a;
                }
                .doc-header-left h1 {
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-size: 22px;
                    font-weight: 700;
                    letter-spacing: -0.02em;
                    margin: 0 0 4px;
                }
                .doc-header-left p {
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-size: 11px;
                    font-weight: 500;
                    color: #888;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    margin: 0;
                }
                .doc-meta {
                    background: #f7f5f2;
                    border: 1px solid #e8e4de;
                    padding: 20px 24px;
                    margin-bottom: 40px;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-size: 13px;
                }
                .doc-meta-row {
                    display: flex;
                    gap: 48px;
                }
                .doc-meta-col label {
                    display: block;
                    font-size: 10px;
                    font-weight: 500;
                    color: #999;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    margin-bottom: 4px;
                }
                .doc-meta-col span {
                    font-weight: 700;
                    color: #1a1a1a;
                }
                .doc-preamble {
                    font-style: italic;
                    font-weight: 500;
                    color: #555;
                    font-size: 14px;
                    margin-bottom: 40px;
                    padding: 20px 24px;
                    border-left: 3px solid #1a1a1a;
                    background: #fafaf9;
                }
                .doc-section {
                    margin-bottom: 32px;
                }
                .doc-section h2 {
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.15em;
                    text-transform: uppercase;
                    color: #888;
                    margin: 0 0 10px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .doc-section h2::after {
                    content: '';
                    flex: 1;
                    height: 1px;
                    background: #e8e4de;
                }
                .doc-section p {
                    font-size: 15px;
                    font-weight: 500;
                    color: #2a2a2a;
                    line-height: 1.85;
                    margin: 0;
                }
                .sig-block {
                    margin-top: 56px;
                    padding-top: 40px;
                    border-top: 2px solid #1a1a1a;
                }
                .sig-block h2 {
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.15em;
                    text-transform: uppercase;
                    color: #888;
                    margin: 0 0 24px;
                }
                .doc-footer {
                    margin-top: 64px;
                    padding-top: 24px;
                    border-top: 1px solid #e8e4de;
                    text-align: center;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-size: 11px;
                    font-weight: 500;
                    color: #bbb;
                    letter-spacing: 0.06em;
                }
                .doc-footer p {
                    margin: 0;
                }

                @page {
                    margin: 20mm 15mm;
                    size: A4;
                }
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; margin: 0 !important; padding: 0 !important; }
                    .doc-outer { background: white !important; padding: 0 !important; }
                    .doc-page {
                        padding: 0 !important;
                        box-shadow: none !important;
                        margin: 0 !important;
                        max-width: 100% !important;
                        width: 100% !important;
                    }
                    .doc-section {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .doc-section h2 {
                        page-break-after: avoid;
                        break-after: avoid;
                    }
                    .sig-block {
                        page-break-before: always;
                        break-before: always;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .doc-preamble,
                    .doc-meta {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    p { orphans: 4; widows: 4; }
                }
                @media (max-width: 700px) {
                    .doc-page {
                        padding: 32px 20px 48px !important;
                    }
                    .doc-meta-row {
                        flex-wrap: wrap;
                        gap: 16px 24px;
                    }
                    .doc-header {
                        flex-direction: column;
                        gap: 16px;
                    }
                    .doc-header img {
                        width: 100px !important;
                    }
                    .doc-outer {
                        padding: 16px 0 40px !important;
                    }
                }
            `}</style>

            <div className="no-print">
                <AdminNav username={username} role={role} avatarUrl={avatarUrl} onLogout={handleLogout} activePath="/admin/agreement" />
            </div>

            <div className="cagreement-content">
                <div className="no-print" style={{ background: theme.card, borderBottom: `1px solid ${theme.border}`, padding: '14px 28px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleDownloadPDF}
                        disabled={!fullName}
                        className={fullName ? 'wlink' : ''}
                        style={fullName ? pillButton : { background: 'none', border: 'none', color: theme.textMuted, padding: 0, fontSize: '12.5px', fontWeight: 700, cursor: 'not-allowed', fontFamily: font }}
                    >
                        {fullName ? t('downloadPdf') : t('loadingLabel')}
                    </button>
                </div>

                <div className="agreement-scope">
                    <div className="doc-outer" style={{ background: '#f0ede8', padding: '48px 0 80px', minHeight: '60vh' }}>
                        <div className="doc-page" id="agreement-doc">

                        <div className="doc-header">
                            <div className="doc-header-left">
                                <h1>Contributor Agreement</h1>
                                <p>winston.ie — Longden Collective</p>
                            </div>
                            <img src={logo} alt="winston.ie" style={{ width: '160px', height: 'auto' }} />
                        </div>

                        <div className="doc-meta">
                            <div className="doc-meta-row">
                                <div className="doc-meta-col">
                                    <label>Prepared for</label>
                                    <span>{fullName}</span>
                                </div>
                                <div className="doc-meta-col">
                                    <label>Created by</label>
                                    <span>Longden Collective</span>
                                </div>
                                <div className="doc-meta-col">
                                    <label>Date</label>
                                    <span>{agreementDate || today}</span>
                                </div>
                                <div className="doc-meta-col">
                                    <label>Platform</label>
                                    <span>winston.ie</span>
                                </div>
                            </div>
                        </div>

                        <div className="doc-preamble">
                            This Contributor Agreement (the "Agreement") is entered into as of {agreementDate || today}, between winston.ie, a label of Longden Collective ("the Platform"), and the contributor identified above ("Contributor"). By publishing creative work on winston.ie, the Contributor acknowledges having read, understood, and agreed to the terms set forth herein.
                        </div>

                        <div className="doc-section">
                            <h2>1 — Public Brand & Administrative Ownership</h2>
                            <p>winston.ie is the public-facing brand under which this platform operates. Administratively, and for purposes of copyright, attribution, and internal organization, winston.ie is a label of Longden Collective. This distinction exists for organizational clarity: contributors and visitors interact with winston.ie as the visible identity, while Longden Collective represents the broader creative ecosystem under which winston.ie, throughmyeyes, and Ardoise each operate as distinct labels.</p>
                        </div>

                        <div className="doc-section">
                            <h2>2 — Copyright & Attribution</h2>
                            <p>The Contributor retains full copyright over all work published on winston.ie. Publishing on this platform does not transfer intellectual property ownership to winston.ie, Longden Collective, or any third party. The Contributor affirms that all submitted work is original and does not infringe upon the rights of any third party. The Contributor will always be credited as the original creator on all content published, shared, or distributed through winston.ie or on its behalf, using the name provided at the time of publishing, which may not be altered without the Contributor's consent. Published works will carry the notice "© [Creator Name] — winston.ie 2026." Site-wide content and platform design carry the separate notice "© winston.ie 2026. All rights reserved. A Longden Collective label."</p>
                        </div>

                        <div className="doc-section">
                            <h2>3 — Creative Catalogue</h2>
                            <p>By publishing on winston.ie, the Contributor's work becomes part of the official winston.ie creative catalogue. winston.ie may present, submit, and exhibit this work as part of its institutional portfolio in competitions, festivals, partnerships, and exhibitions — with full credit attributed to the Contributor as the original creator at all times.</p>
                        </div>

                        <div className="doc-section">
                            <h2>4 — Licence & Primary Publishing Right</h2>
                            <p>The Contributor grants winston.ie / Longden Collective a worldwide, royalty-free licence to publish, display, share, distribute, and use the work as part of the winston.ie platform and its associated projects — including partnerships, collaborations, promotional campaigns, and external exhibitions. This licence remains in effect for the duration of the work's presence on the platform and constitutes a primary, non-exclusive publishing right: the Contributor retains full rights to share, republish, or display the same work on personal social media accounts, portfolios, resumes, or CVs. The Contributor agrees not to publish the same work through another official photography distribution platform, stock agency, or competing curated platform without prior written permission from winston.ie.</p>
                        </div>

                        <div className="doc-section">
                            <h2>5 — Intermediary & Representation</h2>
                            <p>winston.ie acts as the Contributor's label and sole representative for any third-party requests involving published work — including usage requests, reproduction, licensing to external parties, and collaboration opportunities. Any such request will be communicated to the Contributor. The Contributor's explicit written consent is required before any external use is approved. winston.ie will not act on the Contributor's behalf without prior knowledge and consent.</p>
                        </div>

                        <div className="doc-section">
                            <h2>6 — Commercial Revenue</h2>
                            <p>If published work generates commercial revenue through winston.ie — whether through licensing, partnerships, sales, or other means — a revenue-sharing arrangement will be discussed and agreed upon separately with the Contributor prior to any commercial transaction. winston.ie commits to full transparency in all revenue matters involving Contributor work.</p>
                        </div>

                        <div className="doc-section">
                            <h2>7 — Content Standards</h2>
                            <p>The Contributor agrees that submitted content is original work, does not infringe on third-party rights, and complies with winston.ie's community and content guidelines as published from time to time.</p>
                        </div>

                        <div className="doc-section">
                            <h2>8 — Removal & Withdrawal</h2>
                            <p>The Contributor may request removal of their contributed work at any time by contacting winston.ie at contact@winston.longden.site. Removal requests will be processed within 14 days of receipt. Upon removal, the licence granted under Section 4 is terminated for future use; existing third-party agreements entered into prior to the removal request remain valid and enforceable.</p>
                        </div>

                        <div className="doc-section">
                            <h2>9 — No Employment or Partnership</h2>
                            <p>Nothing in this Agreement establishes an employment, partnership, or agency relationship between the Contributor and winston.ie or Longden Collective.</p>
                        </div>

                        <div className="doc-section">
                            <h2>10 — Governing Law</h2>
                            <p>This Agreement is governed by the laws of the Province of Quebec and the federal laws of Canada applicable therein. Any disputes arising under this Agreement shall be resolved in the courts of Montreal, Quebec.</p>
                        </div>

                        <div className="doc-section">
                            <h2>11 — Operating Status</h2>
                            <p>Longden Collective and winston.ie are not, as of the date of this Agreement, legally incorporated entities. This Agreement is made in good faith between the Contributor and the sole owner and operator of these platforms, Andy Le. Should Longden Collective or winston.ie become a formally incorporated entity in the future, this Agreement and its terms will carry forward under that entity without requiring re-signature, unless the Contributor is notified otherwise.</p>
                        </div>

                        <div className="doc-section">
                            <h2>12 — Governing Language</h2>
                            <p>This Agreement is available in French, English, and Vietnamese for the Contributor's convenience. In the event of any discrepancy between versions, the version accepted by the Contributor on the platform shall govern.</p>
                        </div>

                        <div className="sig-block">
                            <h2>Acceptance</h2>
                            <p style={{ fontSize: '14px', fontWeight: 500, color: '#555', lineHeight: '1.85' }}>
                                By creating an account and publishing content on winston.ie, <strong>{fullName}</strong> has agreed to these Contributor Terms as of <strong>{agreementDate || today}</strong>. No physical signature is required — continued use of the platform constitutes full acceptance of this agreement.
                            </p>
                        </div>

                        <div className="doc-footer">
                            <p>© winston.ie 2026. All rights reserved. A Longden Collective label.</p>
                            <p style={{ marginTop: '4px' }}>contact@winston.longden.site — longden.site</p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}

export default Agreement;